import type { LabelFontFamilyId, LabelFontStyle, LabelFontWeight } from '../labels/fonts';
import type { MapView } from '../map/model';
import { LEGACY_COMPOSITION_PIXELS_PER_MM, scaleDenominatorForZoom } from './scale';
import { applyCompositionTemplate } from './templates';

export const COMPOSITION_PAGE_PRESETS = ['a5', 'a4', 'a3', 'letter', 'custom'] as const;
export type CompositionPagePreset = (typeof COMPOSITION_PAGE_PRESETS)[number];

export const COMPOSITION_CUSTOM_PAGE_MIN_MM = 100;
export const COMPOSITION_CUSTOM_PAGE_MAX_MM = 420;
export const COMPOSITION_GRID_SPACING_MM = 10;
export const COMPOSITION_GRID_SPACING_MIN_MM = 2;
export const COMPOSITION_GRID_SPACING_MAX_MM = 20;
export const COMPOSITION_TYPOGRAPHY_MIN_PT = 4;
export const COMPOSITION_SOURCES_ATTRIBUTION_MIN_PT = 5;
export const COMPOSITION_SOURCES_ATTRIBUTION_MAX_PT = 12;
export const COMPOSITION_SCALE_BAR_THICKNESS_MIN_MM = 0.1;
export const COMPOSITION_SCALE_BAR_THICKNESS_MAX_MM = 2;
export const COMPOSITION_SCALE_BAR_THICKNESS_DEFAULT_MM = 0.45;
export const COMPOSITION_LEGEND_SPACING_MAX_MM = 50;
export const COMPOSITION_LEGEND_CORNER_RADIUS_MAX_MM = 50;
export const COMPOSITION_LEGEND_PADDING_HORIZONTAL_DEFAULT_MM = 2.4;
export const COMPOSITION_LEGEND_PADDING_VERTICAL_DEFAULT_MM = 2.1;
export const COMPOSITION_LEGEND_GROUP_SPACING_DEFAULT_MM = 1.6;
export const COMPOSITION_LEGEND_ITEM_SPACING_DEFAULT_MM = 0.8;

export const COMPOSITION_PAGE_ORIENTATIONS = ['landscape', 'portrait'] as const;
export type CompositionPageOrientation = (typeof COMPOSITION_PAGE_ORIENTATIONS)[number];

export interface NormalizedPoint {
  readonly x: number;
  readonly y: number;
}

export interface NormalizedRect extends NormalizedPoint {
  readonly width: number;
  readonly height: number;
}

export interface CompositionPage {
  readonly preset: CompositionPagePreset;
  readonly orientation: CompositionPageOrientation;
  readonly backgroundColour: string;
  readonly scaleLayoutWithPage: boolean;
  readonly customWidthMm: number | null;
  readonly customHeightMm: number | null;
}

export type CompositionTextAlignment = 'left' | 'centre' | 'right';

export interface CompositionTypography {
  readonly fontFamily: LabelFontFamilyId;
  readonly fontWeight: LabelFontWeight;
  readonly fontStyle: LabelFontStyle;
  readonly fontSizePt: number;
  readonly colour: string;
  readonly alignment: CompositionTextAlignment;
}

export interface CompositionMapFrame {
  readonly cornerRadiusMm?: number;
  readonly bounds: NormalizedRect;
  readonly camera: CompositionCamera;
  readonly border: {
    readonly enabled: boolean;
    readonly widthMm: number;
    readonly colour: string;
  };
}

export interface CompositionCamera {
  readonly center: readonly [longitude: number, latitude: number];
  readonly scaleDenominator: number;
  readonly bearing: number;
  readonly pitch: number;
}

export interface CompositionTextElement {
  readonly enabled: boolean;
  readonly text: string;
  readonly position: NormalizedPoint;
  readonly typography: CompositionTypography;
}

export interface CompositionLegend {
  /** Absent in earlier schema-v1 projects means one column. */
  readonly columnCount?: 1 | 2 | 3;
  /** Missing means true; hidden repeats reserve the original group-heading footprint. */
  readonly repeatContinuationHeadings?: boolean;
  readonly symbolSizePercent?: number;
  /** Generic auto-fit anchor; absence preserves historical bottom-right fitting. */
  readonly autoFitAnchor?: 'top-right' | 'bottom-right';
  readonly enabled: boolean;
  readonly autoFit: boolean;
  readonly bounds: NormalizedRect;
  readonly background: {
    readonly enabled: boolean;
    readonly colour: string;
    readonly opacity: number;
  };
  readonly border: {
    readonly enabled: boolean;
    readonly widthMm: number;
    readonly colour: string;
  };
  readonly cornerRadiusMm: number;
  readonly paddingTopMm: number;
  readonly paddingRightMm: number;
  readonly paddingBottomMm: number;
  readonly paddingLeftMm: number;
  readonly groupSpacingMm: number;
  readonly itemSpacingMm: number;
  readonly bodyTypography: CompositionTypography;
  readonly heading: {
    readonly enabled: boolean;
    readonly text: string;
    readonly typography: CompositionTypography;
  };
  readonly excludedLayerIds: readonly string[];
  /** Stable legend-group IDs whose otherwise eligible headings are hidden. */
  readonly hiddenGroupHeadingIds: readonly string[];
  readonly excludedEntryKeysByLayer: Readonly<Record<string, readonly string[]>>;
}

export const COMPOSITION_SCALE_BAR_UNITS = ['metric', 'imperial'] as const;
export type CompositionScaleBarUnits = (typeof COMPOSITION_SCALE_BAR_UNITS)[number];

export interface CompositionScaleBar {
  readonly style?: 'line' | 'segmented' | 'simple-segmented';
  readonly labelPosition?: 'above' | 'below';
  readonly panel?: CompositionPanelAppearance;
  readonly enabled: boolean;
  readonly position: NormalizedPoint;
  readonly units: CompositionScaleBarUnits;
  readonly maximumWidthMm: number;
  readonly colour: string;
  readonly fontSizePt: number;
  readonly thicknessMm: number;
}

/** Uses the same physical background/border/radius vocabulary as the legend and inset. */
export interface CompositionPanelAppearance {
  readonly background: CompositionLegend['background'];
  readonly border: CompositionMapFrame['border'];
  readonly cornerRadiusMm: number;
  readonly paddingMm: number;
}

export const COMPOSITION_NORTH_ARROW_STYLES = [
  'outline',
  'filled',
  'circle-filled',
  'circle-hollow',
  'cross',
  'arrow',
  'triangle',
  'circle-arrow',
  'circle-classic',
] as const;
export type CompositionNorthArrowStyle = (typeof COMPOSITION_NORTH_ARROW_STYLES)[number];

export interface CompositionNorthArrow {
  readonly enabled: boolean;
  readonly position: NormalizedPoint;
  readonly sizeMm: number;
  readonly colour: string;
  readonly showLabel: boolean;
  readonly style: CompositionNorthArrowStyle;
}

export interface CompositionSourcesAttribution {
  /** Optional normalized text-box width; older files retain their legacy remaining-width box. */
  readonly width?: number;
  /** Position is authored; mandatory wording is generated from the sources currently in use. */
  readonly position: NormalizedPoint;
  readonly typography: CompositionTypography;
}

export type CompositionLogoMimeType = 'image/jpeg' | 'image/png';

export interface CompositionLogo {
  readonly filename?: string;
  readonly mimeType: CompositionLogoMimeType;
  readonly dataUrl: string;
  readonly aspectRatio: number;
  readonly bounds: NormalizedRect;
}

export interface CompositionInsetMap {
  /** Missing in older projects means visible; hiding keeps all configured content. */
  readonly enabled?: boolean;
  readonly hiddenLayerIds: readonly string[];
  readonly hiddenAnnotationLayerIds: readonly string[];
  readonly hiddenAnnotationIds: readonly string[];
  readonly bounds: NormalizedRect;
  readonly contextMargin: number;
  readonly border: CompositionMapFrame['border'];
  readonly cornerRadiusMm: number;
  readonly indicator: {
    readonly outlineColour: string;
    readonly outlineWidthMm: number;
    readonly fillColour: string;
    readonly fillOpacity: number;
  };
}

/** The MVP intentionally authors exactly one renderer-independent composition per project. */
export interface CompositionDraft {
  readonly page: CompositionPage;
  readonly mapFrame: CompositionMapFrame;
  readonly title: CompositionTextElement;
  readonly subtitle: CompositionTextElement;
  readonly legend: CompositionLegend;
  readonly scaleBar: CompositionScaleBar;
  readonly northArrow: CompositionNorthArrow;
  readonly sourcesAttribution: CompositionSourcesAttribution;
  /** One small, self-contained PNG/JPEG logo. Multiple graphics remain deliberately out of scope. */
  readonly logo: CompositionLogo | null;
  readonly insetMap: CompositionInsetMap | null;
}

export function createLegacyComposition(camera: MapView, title: string): CompositionDraft {
  const scaleDenominator =
    scaleDenominatorForZoom(camera.zoom, camera.center[1], LEGACY_COMPOSITION_PIXELS_PER_MM) ??
    50_000;
  return {
    page: {
      preset: 'a4',
      orientation: 'landscape',
      backgroundColour: '#ffffff',
      scaleLayoutWithPage: true,
      customWidthMm: null,
      customHeightMm: null,
    },
    mapFrame: {
      bounds: { x: 0.055, y: 0.16, width: 0.89, height: 0.785 },
      camera: {
        center: [...camera.center],
        scaleDenominator,
        bearing: camera.bearing,
        pitch: camera.pitch,
      },
      border: { enabled: true, widthMm: 0.4, colour: '#52606d' },
    },
    title: {
      enabled: true,
      text: title,
      position: { x: 0.055, y: 0.045 },
      typography: {
        fontFamily: 'inter',
        fontWeight: 'bold',
        fontStyle: 'normal',
        fontSizePt: 22,
        colour: '#17212b',
        alignment: 'left',
      },
    },
    subtitle: {
      enabled: false,
      text: '',
      position: { x: 0.055, y: 0.105 },
      typography: {
        fontFamily: 'inter',
        fontWeight: 'regular',
        fontStyle: 'normal',
        fontSizePt: 11,
        colour: '#17212b',
        alignment: 'left',
      },
    },
    legend: {
      enabled: true,
      repeatContinuationHeadings: true,
      autoFit: true,
      bounds: {
        x: 0.945 - 5 / 297 - 0.168,
        y: 0.945 - 5 / 210 - 0.221,
        width: 0.168,
        height: 0.221,
      },
      background: { enabled: true, colour: '#ffffff', opacity: 90 },
      border: { enabled: true, widthMm: 0.3, colour: '#52606d' },
      cornerRadiusMm: 0,
      paddingTopMm: COMPOSITION_LEGEND_PADDING_VERTICAL_DEFAULT_MM,
      paddingRightMm: COMPOSITION_LEGEND_PADDING_HORIZONTAL_DEFAULT_MM,
      paddingBottomMm: COMPOSITION_LEGEND_PADDING_VERTICAL_DEFAULT_MM,
      paddingLeftMm: COMPOSITION_LEGEND_PADDING_HORIZONTAL_DEFAULT_MM,
      groupSpacingMm: COMPOSITION_LEGEND_GROUP_SPACING_DEFAULT_MM,
      itemSpacingMm: COMPOSITION_LEGEND_ITEM_SPACING_DEFAULT_MM,
      bodyTypography: {
        fontFamily: 'inter',
        fontWeight: 'regular',
        fontStyle: 'normal',
        fontSizePt: 9,
        colour: '#17212b',
        alignment: 'left',
      },
      heading: {
        enabled: true,
        text: 'Legend',
        typography: {
          fontFamily: 'inter',
          fontWeight: 'bold',
          fontStyle: 'normal',
          fontSizePt: 11,
          colour: '#17212b',
          alignment: 'left',
        },
      },
      excludedLayerIds: [],
      hiddenGroupHeadingIds: [],
      excludedEntryKeysByLayer: {},
    },
    scaleBar: {
      enabled: false,
      position: { x: 0.085, y: 0.87 },
      units: 'metric',
      maximumWidthMm: 40,
      colour: '#17212b',
      fontSizePt: 9,
      thicknessMm: COMPOSITION_SCALE_BAR_THICKNESS_DEFAULT_MM,
    },
    northArrow: {
      enabled: false,
      position: { x: 0.87, y: 0.76 },
      sizeMm: 18,
      colour: '#000000',
      showLabel: true,
      style: 'outline',
    },
    sourcesAttribution: {
      position: { x: 0.055, y: 0.95 },
      typography: {
        fontFamily: 'inter',
        fontWeight: 'regular',
        fontStyle: 'normal',
        fontSizePt: 7.5,
        colour: '#52606d',
        alignment: 'left',
      },
    },
    logo: null,
    insetMap: null,
  };
}

export function createDefaultComposition(camera: MapView, title: string): CompositionDraft {
  // New-project authoring only; saved projects normalize against createLegacyComposition.
  // Reuse the editable Report recipe so first entry and manual application cannot drift.
  return applyCompositionTemplate(createLegacyComposition(camera, title), 'report');
}
